import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import FormPopup from '../components/FormPopup';
import HeroSection from '../components/HeroSection';

const Contact: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen">
      {/* CONSULTATION-FOCUSED HERO SECTION */}
      <HeroSection
        desktopImage="/images/hero-section-3.png"
        mobileImage="/images/mobile-hero-3.png"
        title={<span className="text-gradient">Begin Your Journey</span>}
        subtitle="Transform your vision into reality with our team of specialists. From initial consultation to final calibration, we guide you through every step of creating your ultimate cinematic experience."
        tagline="Complimentary Consultation • White-Glove Service • Lifetime Support"
      />

      {/* CONSULTATION OPTIONS SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                How We Can Help
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Choose the consultation approach that best fits your project and timeline
              </p>
            </motion.div>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Private Showroom Experience */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-highlight text-2xl">🏛️</span>
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-gradient text-center">Private Showroom</h3>
                <p className="text-secondary/80 mb-6 text-center">
                  Experience our complete collection in our state-of-the-art facility. 
                  See, hear, and feel the difference that precision engineering makes.
                </p>
                
                <div className="space-y-3 mb-6 text-sm text-secondary/70">
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>All four products on display</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>Golf and flight simulators</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>1-on-1 specialist consultation</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>By appointment only</span>
                  </div>
                </div>
              </motion.div>
              
              {/* In-Home Consultation */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-highlight text-2xl">🏠</span>
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-gradient text-center">In-Home Consultation</h3>
                <p className="text-secondary/80 mb-6 text-center">
                  Our specialists visit your space to create custom solutions that 
                  integrate seamlessly with your architecture and lifestyle.
                </p>
                
                <div className="space-y-3 mb-6 text-sm text-secondary/70">
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>Space analysis and measurement</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>Custom design proposals</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>Investment planning</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>No obligation</span>
                  </div>
                </div>
              </motion.div>
              
              {/* Virtual Consultation */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-highlight text-2xl">💻</span>
                </div>
                <h3 className="text-2xl font-semibold mb-4 text-gradient text-center">Virtual Consultation</h3>
                <p className="text-secondary/80 mb-6 text-center">
                  Start your journey with a detailed virtual session. Perfect for 
                  initial planning or when distance is a factor.
                </p>
                
                <div className="space-y-3 mb-6 text-sm text-secondary/70">
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>45-minute guided session</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>Product demonstrations</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>Space planning guidance</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-1.5 h-1.5 bg-highlight rounded-full mr-3"></div>
                    <span>Available worldwide</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT INFORMATION & QUICK FORM */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-light mb-8 text-gradient">
                  Connect With Our Specialists
                </h2>
                
                <div className="space-y-8">
                  {/* Phone */}
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mr-6 flex items-center justify-center flex-shrink-0">
                      <span className="text-highlight text-xl">📞</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gradient">Direct Line</h3>
                      <p className="text-2xl font-light text-white mb-2">(555) 123-CINEMA</p>
                      <p className="text-secondary/80 text-sm">Monday - Friday: 9:00 AM - 7:00 PM PST</p>
                      <p className="text-secondary/80 text-sm">Saturday: 10:00 AM - 4:00 PM PST</p>
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mr-6 flex items-center justify-center flex-shrink-0">
                      <span className="text-highlight text-xl">✉️</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gradient">Email Consultation</h3>
                      <p className="text-xl font-light text-white mb-2">specialists@parallaxcinema.com</p>
                      <p className="text-secondary/80 text-sm">Detailed responses within 4 hours</p>
                    </div>
                  </div>
                  
                  {/* Showroom */}
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-gradient-radial from-highlight/30 to-transparent rounded-full mr-6 flex items-center justify-center flex-shrink-0">
                      <span className="text-highlight text-xl">📍</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2 text-gradient">Private Showroom</h3>
                      <p className="text-xl font-light text-white mb-2">By Appointment Only</p>
                      <p className="text-secondary/80 text-sm">Los Angeles, California</p>
                      <p className="text-secondary/80 text-sm">Precise location provided upon scheduling</p>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Quick Contact Form */}
              <motion.div 
                className="glass-effect rounded-2xl p-8"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-semibold mb-6 text-gradient text-center">
                  Quick Consultation Request
                </h3>
                
                {/* Embedded GoHighLevel Form */}
                <div className="mb-8">
                  <iframe
                    src="https://api.leadconnectorhq.com/widget/form/agfreWNoXzwzmApfwMVP"
                    style={{ width: "100%", height: "510px", border: "none", borderRadius: "12px" }}
                    title="Contact Form - Parallax Cinema"
                    loading="lazy"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREA & PROCESS */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-light mb-8 text-gradient">
                Our Service Promise
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                From initial consultation to lifetime support, experience the difference 
                that true expertise and dedication makes.
              </p>
            </motion.div>
            
            <div className="grid lg:grid-cols-2 gap-16">
              {/* Service Areas */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-semibold mb-6 text-gradient">Service Areas</h3>
                <div className="space-y-4">
                  <div className="glass-effect rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Primary Markets</h4>
                    <p className="text-secondary/80 text-sm">
                      Los Angeles, Orange County, San Diego, San Francisco Bay Area, 
                      Las Vegas, Phoenix, Denver
                    </p>
                  </div>
                  
                  <div className="glass-effect rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">National Service</h4>
                    <p className="text-secondary/80 text-sm">
                      Complete installation services available throughout the United States. 
                      Travel and accommodation included in project pricing.
                    </p>
                  </div>
                  
                  <div className="glass-effect rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">International</h4>
                    <p className="text-secondary/80 text-sm">
                      Consultation and product delivery available worldwide. 
                      Installation partnerships in major international markets.
                    </p>
                  </div>
                </div>
              </motion.div>
              
              {/* Process Timeline */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-semibold mb-6 text-gradient">Our Process</h3>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-highlight rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-primary text-sm font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Initial Consultation</h4>
                      <p className="text-secondary/80 text-sm">Understand your vision, space, and requirements</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-highlight rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-primary text-sm font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Custom Design</h4>
                      <p className="text-secondary/80 text-sm">Detailed proposals with 3D renderings and specifications</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-highlight rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-primary text-sm font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Professional Installation</h4>
                      <p className="text-secondary/80 text-sm">White-glove installation by certified specialists</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-highlight rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-primary text-sm font-bold">4</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Calibration & Training</h4>
                      <p className="text-secondary/80 text-sm">Precision tuning and comprehensive user training</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-highlight rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-primary text-sm font-bold">5</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Lifetime Support</h4>
                      <p className="text-secondary/80 text-sm">Ongoing maintenance, updates, and optimization</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CALL TO ACTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Ready to Begin?
              </h2>
              <p className="text-xl text-secondary/80 mb-12 max-w-3xl mx-auto">
                Your journey to cinematic excellence starts with a simple conversation. 
                Connect with our specialists today to explore the possibilities.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button 
                  onClick={() => setIsFormOpen(true)}
                  className="btn-primary text-lg px-12 py-4"
                >
                  Schedule Consultation
                </button>
                <button className="btn-secondary text-lg px-12 py-4">
                  Call (555) 123-CINEMA
                </button>
              </div>
              
              <div className="mt-8 text-secondary/60 text-sm">
                All consultations are complimentary and include custom design proposals
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FormPopup */}
      <FormPopup 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
      />
    </div>
  );
};

export default Contact; 
